import { useMemo } from "react";
import { Typography } from "@/components/atoms";
import { Loading } from "@/components/atoms/Loading/Loading";
import { ComparePlans, useSyncCardHeights } from "@/components/organisms";
import { SubscriptionCard } from "@/components/organisms/SubscriptionPlan/SubscriptionCard";
import { useGetSubscriptionPlansQuery } from "@/redux/apis/subscription/subscriptionPlansApi";

/**
 * Embeddable read-only subscription plan page.
 * Designed to be embedded via iframe on external websites.
 * Uses only the public /v1/subscription-plans endpoint — no authentication required.
 */
export const SubscriptionPlan = () => {
  const { data: subscriptionData, isLoading, error } = useGetSubscriptionPlansQuery();

  const numSubscriptions = subscriptionData?.subscriptions.length || 3;
  const cardRefs = useSyncCardHeights(numSubscriptions, !isLoading && !!subscriptionData);

  // Map subscription IDs to display properties
  const subscriptionDisplayConfig = useMemo(() => {
    if (!subscriptionData) return {};

    const config: Record<string, { showRecommended: boolean; showMostPopular: boolean }> = {};

    subscriptionData.subscriptions.forEach((sub) => {
      if (sub.subscriptionId === "starter-rider-plan") {
        config[sub.subscriptionId] = { showRecommended: true, showMostPopular: false };
      } else if (sub.subscriptionId === "basic-provider-plan") {
        config[sub.subscriptionId] = { showRecommended: false, showMostPopular: true };
      } else {
        config[sub.subscriptionId] = { showRecommended: false, showMostPopular: false };
      }
    });

    return config;
  }, [subscriptionData]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-transparent p-6">
        <Loading />
      </div>
    );
  }

  if (error || !subscriptionData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-transparent p-6">
        <Typography variant="h3" className="text-error-primary">
          Error loading subscription plans
        </Typography>
        <Typography variant="paragraph" className="mt-2">
          Please try again later
        </Typography>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div className="flex flex-col text-neutral-dark-gray">
        <div className="flex w-full flex-col items-center gap-1 text-center">
          <Typography variant="h2">Upgrade your plan for more features</Typography>
        </div>
        <div className="mt-10 grid w-full grid-cols-1 gap-5 lg:grid-cols-3">
          {subscriptionData.subscriptions.map((subscription, index) => {
            const refs = cardRefs[index] || { headerRef: null, coreRef: null };
            const displayConfig = subscriptionDisplayConfig[subscription.subscriptionId] || {
              showRecommended: false,
              showMostPopular: false,
            };

            return (
              <SubscriptionCard
                key={subscription.subscriptionId}
                subscription={subscription}
                allSubscriptionData={subscriptionData}
                headerRef={refs.headerRef}
                coreRef={refs.coreRef}
                showRecommended={displayConfig.showRecommended}
                showMostPopular={displayConfig.showMostPopular}
                hideActions
              />
            );
          })}
        </div>
      </div>
      <ComparePlans className="mt-16" hideActions />
    </div>
  );
};
